import { useEffect, useState } from 'react'
import './styles.css'
import { savedKey, useSavedKundlis } from './useSavedKundlis'
import SavedKundliFilters, { NO_FILTERS } from './SavedKundliFilters'
import EditKundliModal from './EditKundliModal'

function fmtDate(iso) {
  const d = new Date(`${iso}T12:00:00`)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function monogram(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  return parts.length ? (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase() : '🪐'
}

/** The first, most specific part of a geocoded place — "Kolkata" of "Kolkata, West Bengal, India". */
const shortPlace = (place) => String(place || '').split(',')[0].trim()

/**
 * The signed-in user's kundli history. Clicking a row hands the saved birth
 * details to `onOpen`, which re-casts the chart live — nothing but the inputs
 * is stored. Used inside the studio and by the astrologer console's picker.
 * ✎ on a row opens the editor (birth details, label & notes — same id kept).
 *
 * `filterable` (the seeker dashboard's Saved Kundlis tab) swaps the compact
 * search box for the full filter bar — gender, source, birth-date range and
 * sort, all applied server-side — and lets the list grow to the page.
 */
export default function SavedKundlis({ onOpen, activeKey = '', filterable = false }) {
  const [search, setSearch] = useState('')
  const [term, setTerm] = useState('')
  const [filters, setFilters] = useState(NO_FILTERS)
  const [confirmId, setConfirmId] = useState(null)
  const [editing, setEditing] = useState(null) // the saved row open in the editor
  const {
    kundlis, total, isLoading, isFetching, isError, refetch, remove, removingId, hasMore, loadMore, loadingMore, updateBirth,
  } = useSavedKundlis({ search: term, ...(filterable ? filters : {}) })

  // Debounce the server-side search so typing doesn't fire a request per key.
  useEffect(() => {
    const t = setTimeout(() => setTerm(search), 300)
    return () => clearTimeout(t)
  }, [search])

  // A pending delete confirmation quietly lapses if it isn't confirmed.
  useEffect(() => {
    if (confirmId == null) return
    const t = setTimeout(() => setConfirmId(null), 4000)
    return () => clearTimeout(t)
  }, [confirmId])

  const askOrDelete = async (id) => {
    if (confirmId !== id) {
      setConfirmId(id)
      return
    }
    setConfirmId(null)
    try {
      await remove(id)
    } catch (err) {
      console.warn('[kundli-studio] could not delete kundli', err)
    }
  }

  // Sort only reorders — the "no matches" copy is for filters that narrow.
  const narrowed = Boolean(term || filters.gender || filters.source || filters.birth_date_from || filters.birth_date_to)
  const showSearch = !filterable && (Boolean(term) || total > 6)
  const clearAll = () => {
    setSearch('')
    setTerm('')
    setFilters(NO_FILTERS)
  }

  return (
    <div className={`ks-saved${filterable ? ' ks-saved--page' : ''}`}>
      <div className="ks-saved-head">
        <p className="ks-saved-title">
          {filterable ? 'Saved Kundlis' : 'My Kundlis'}
          {total > 0 && <span className="ks-saved-count">{total}</span>}
        </p>
        {showSearch && (
          <input
            type="search"
            className="field ks-saved-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or place…"
            aria-label="Search my kundlis"
          />
        )}
      </div>

      {filterable && (
        <SavedKundliFilters
          search={search}
          onSearch={setSearch}
          filters={filters}
          onChange={setFilters}
          onClear={clearAll}
          canClear={narrowed || Boolean(filters.sort)}
        />
      )}

      {isLoading ? (
        <ul className="ks-saved-list" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <li key={i} className="ks-saved-item ks-saved-item--skel">
              <span className="ks-skel ks-pulse ks-saved-skel" />
            </li>
          ))}
        </ul>
      ) : isError ? (
        <p className="ks-saved-empty">
          We couldn&rsquo;t load your saved kundlis.{' '}
          <button type="button" className="ks-saved-retry" onClick={() => refetch()}>Try again</button>
        </p>
      ) : kundlis.length === 0 ? (
        <p className="ks-saved-empty">
          {narrowed
            ? `No saved kundli matches ${filterable ? 'these filters' : 'that search'}.`
            : 'No saved kundlis yet — every chart you cast is kept here automatically.'}
        </p>
      ) : (
        <ul className={`ks-saved-list${isFetching ? ' ks-saved-list--busy' : ''}`}>
          {kundlis.map((k) => {
            const active =
              activeKey &&
              activeKey === savedKey({ dob: k.birth_date, tob: k.birth_time, lat: k.latitude, lon: k.longitude, name: k.name })
            const confirming = confirmId === k.id
            return (
              <li key={k.id} className={`ks-saved-item${active ? ' is-active' : ''}`}>
                <button type="button" className="ks-saved-open" onClick={() => onOpen(k)} title={k.place_name}>
                  <span className="ks-saved-avatar" aria-hidden="true">{monogram(k.name)}</span>
                  <span className="ks-saved-text">
                    <span className="ks-saved-name">
                      {k.name || 'Unnamed chart'}
                      {k.label && <span className="ks-saved-label">{k.label}</span>}
                    </span>
                    <span className="ks-saved-meta">
                      {fmtDate(k.birth_date)} · {k.birth_time} · {shortPlace(k.place_name)}
                    </span>
                  </span>
                  {active && <span className="ks-saved-badge">Open</span>}
                </button>
                <button
                  type="button"
                  className="ks-saved-edit"
                  onClick={() => setEditing(k)}
                  aria-label={`Edit ${k.name || 'chart'}`}
                  title="Edit details"
                >
                  ✎
                </button>
                <button
                  type="button"
                  className={`ks-saved-del${confirming ? ' is-confirming' : ''}`}
                  onClick={() => askOrDelete(k.id)}
                  disabled={removingId === k.id}
                  aria-label={confirming ? `Confirm delete ${k.name || 'chart'}` : `Delete ${k.name || 'chart'}`}
                  title={confirming ? 'Click again to delete' : 'Delete'}
                >
                  {removingId === k.id ? '…' : confirming ? 'Delete?' : '✕'}
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {hasMore && !isLoading && !isError && (
        <div className="ks-saved-more">
          <button type="button" className="ks-ghost-btn" onClick={() => loadMore()} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : `Show more (${kundlis.length} of ${total})`}
          </button>
        </div>
      )}

      {editing && (
        <EditKundliModal
          kundli={editing}
          onSave={(birth, extra) => updateBirth(editing.id, birth, extra)}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}
